import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import ArticleCard from '@/components/base/ArticleCard';
import CategorySidebar from '@/pages/category/components/CategorySidebar';
import { getCategoryBySlug, getArticlesByCategory, sortArticles } from '@/lib/content';
import { usePageMeta } from '@/hooks/usePageMeta';
import type { Article } from '@/types/content';

const PAGE_SIZE = 6;

const sortOptions = [
  { key: 'new', label: '最新发布', icon: 'ri-time-line' },
  { key: 'hot', label: '最多围观', icon: 'ri-fire-line' },
  { key: 'like', label: '最多点赞', icon: 'ri-thumb-up-line' },
];

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = getCategoryBySlug(slug);
  const [sort, setSort] = useState('new');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);

  usePageMeta(
    category ? `${category.name} - 猎奇吃瓜` : '栏目不存在 - 猎奇吃瓜',
    category ? category.desc : '这个栏目暂时没有内容，换个栏目继续吃瓜。',
  );

  const list = useMemo(() => {
    const base = getArticlesByCategory(slug);
    const filtered = keyword.trim()
      ? base.filter(
          (item: Article) =>
            item.title.includes(keyword.trim()) ||
            item.tags.some((tag: string) => tag.includes(keyword.trim())),
        )
      : base;
    return sortArticles(filtered, sort);
  }, [slug, sort, keyword]);

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-700">
            <i className="ri-emotion-sad-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl text-foreground-950">这个栏目还没开张</h1>
          <p className="mt-3 text-sm text-foreground-600">换个栏目看看，瓜田里还有很多好玩的。</p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 dark:text-foreground-950"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 猎奇吃瓜`,
    description: category.desc,
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: list.map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://ff41523.cn/article/${item.id}`,
        name: item.title,
      })),
    },
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <JsonLd data={collectionSchema} />
      <SiteHeader />
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '主题分类', to: '/' },
          { label: category.name },
        ]}
      />
      <main className="flex-1 px-4 py-10 md:px-8 md:py-12">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <header className="rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-2xl text-background-50 dark:text-foreground-950">
                <i className={category.icon} aria-hidden="true"></i>
              </span>
              <h1 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">
                {category.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {category.desc}
              </p>
              <p className="mt-4 text-xs text-foreground-500">
                共 {category.count} 篇内容 · 当前栏目已展示 {list.length} 篇
              </p>
            </header>

            <div className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap gap-1 rounded-full bg-background-100 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      setSort(opt.key);
                      setPage(1);
                    }}
                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      sort === opt.key
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={opt.icon} aria-hidden="true"></i>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2 md:w-64">
                <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
                <input
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                  placeholder="在本栏目内搜索标题或标签"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  aria-label="本栏目内搜索"
                />
              </div>
            </div>

            {paged.length === 0 ? (
              <p className="mt-6 rounded-lg border border-dashed border-background-300 py-20 text-center text-sm text-foreground-500">
                没有匹配的内容，试试换个关键词。
              </p>
            ) : (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {paged.map((item: Article) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <nav className="mt-8 flex items-center justify-center gap-2" aria-label="分页导航">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={current === 1}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setPage(idx + 1)}
                    className={`h-9 w-9 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                      current === idx + 1
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'border border-background-300 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={current === totalPages}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </button>
              </nav>
            )}
          </div>

          <CategorySidebar activeSlug={slug} />
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}